import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { isMotionReduced } from '#src/app/shared/utils/accessibility';

// Locks the scroll-spy briefly after a click so the smooth-scroll animation passing
// through intermediate sections doesn't flicker the active dot before settling.
const SCROLL_SPY_LOCK_MS = 700;

type Result = ModelHookResult<
	{
		activeId: string | null;
	},
	{
		scrollToSection: (id: string) => void;
	}
>;

/**
 * Scroll-spy for the ambassadors page side nav: an IntersectionObserver watches the
 * section elements identified by `sectionIds` and reports which one is currently in
 * view (via the "-20% 0px -60% 0px" root margin, i.e. a band near the top of the
 * viewport); `scrollToSection` smooth-scrolls (respecting reduced-motion) to a given
 * section. No-ops gracefully on the server and when IntersectionObserver isn't
 * available. `sectionIds` must be a stable reference across renders (e.g. a
 * module-level constant).
 */
export function useScrollSpy(sectionIds: readonly string[]): Result {
	const [activeId, setActiveId] = useState<string | null>(null);
	const lockUntilRef = useRef(0);

	const isIntersectionObserverSupported = useMemo(
		() => typeof window !== 'undefined' && typeof IntersectionObserver !== 'undefined',
		[],
	);

	const scrollToSection = useCallback((id: string) => {
		const section = document.getElementById(id);
		if (!section) return;

		setActiveId(id);
		lockUntilRef.current = Date.now() + SCROLL_SPY_LOCK_MS;
		section.scrollIntoView({ behavior: isMotionReduced() ? 'auto' : 'smooth' });
	}, []);

	useEffect(() => {
		if (!isIntersectionObserverSupported) return undefined;

		const observer = new IntersectionObserver(
			(entries) => {
				if (Date.now() < lockUntilRef.current) return;

				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						setActiveId(entry.target.id);
					}
				});
			},
			{ rootMargin: '-20% 0px -60% 0px', threshold: 0 },
		);

		sectionIds.forEach((id) => {
			const section = document.getElementById(id);
			if (section) observer.observe(section);
		});

		return () => observer.disconnect();
	}, [sectionIds, isIntersectionObserverSupported]);

	return { data: { activeId }, actions: { scrollToSection } };
}
